<template>
	<a
		:href="`/customer-stories/${ story.slug }/`"
		class="card"
		:class="{ 'is-full-width': isWide }"
		@click="trackAnalyticsEvent"
	>
		<figure
			class="card-image"
			v-if="story.featured_image"
			ref="image"
		>
			<img :src="imageUrl" :alt="`${story.acf.customer.post_title} customer story`" loading="lazy" />
		</figure>

		<article class="card-content">
			<h4 class="subtitle" ref="subtitle">
				<span v-if="story.taxonomies.industry" v-html="formatTaxonomyList(story.taxonomies.industry)"></span>
				<template v-else>Customer story</template>
			</h4>

			<h3 class="title" v-html="story.acf.customer.post_title"></h3>

			<p class="description" v-html="story.acf.summary"></p>
		</article>

		<aside class="card-content">
			<figure class="quote">
				<blockquote v-html="story.acf.hero.quote"></blockquote>
				<figcaption>
					<h6 class="title is-5 mb-1" v-html="story.acf.hero.quote_author || story.acf.hero.quote_author_title"></h6>
					<p v-html="story.acf.customer.post_title"></p>
				</figcaption>
			</figure>
			<p class="button is-link">Read customer story</p>
		</aside>
	</a>
</template>

<script>

import analytics from 'Scripts/analytics';
import formatTaxonomyList from 'Lib/helpers/format/taxonomyList.js';
import image from '@gearset/web-core/helpers/image.js';

export default {
	computed: {
		/* Whether the current story is displayed wide or not */
		isWide() {
			return this.columnSize === 'is-half-tablet is-12-desktop';
		},

		/* Get image URL from the story */
		imageUrl() {
			return image(this.story, 'card');
		},
	},

	methods: {
		formatTaxonomyList,

		/* Track card click for analytics */
		trackAnalyticsEvent() {
			analytics.trackAnalyticsEvent('cta', 'click', 'customer-stories-hub');
		}
	},

	props: {
		story: {
			type: Object,
			required: true,
		},

		columnSize: {
			type: String,
		},
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.card {
	overflow: hidden;

	* {
		pointer-events: none;
	}

	article.card-content > * {
		transition: transform .25s, opacity .25s;
	}

	.card-image {
		transition: transform .35s, opacity .35s;
	}

	article.card-content > *, .card-image {
		transition-delay: .3s;
		transition-timing-function: cubic-bezier(.25, 1, .5, 1);
	}

	article.card-content > :nth-child(1) {
		transition-delay: .25s;
	}

	article.card-content > :nth-child(2) {
		transition-delay: .2s;
	}

	article.card-content > :nth-child(3) {
		transition-delay: .15s;
	}

	aside.card-content {
		position: absolute;
		top: 0;
		width: 100%;

		.button, figcaption, blockquote {
			transition: transform .25s, opacity .25s;
			opacity: 0;
			transform: translateX(-1.5rem);
			transition-delay: 0s;
			transition-timing-function: cubic-bezier(.5, 0, .75, 0);
		}

		.quote {
			margin-bottom: 1rem;
		}

		blockquote {
			font-size: $size-5;

			&::before {
				font-size: 3rem;
				top: -2.5rem;
			}

			:deep(*) {
				font-weight: inherit !important;
			}

			:deep(p) {
				font-size: $size-5 !important;
			}
		}

		figcaption {
			.title {
				font-size: $size-6 !important;
			}

			img {
				max-height: 2.25rem;
				max-width: 4.5rem;
			}
		}
	}

	&:hover {
		article.card-content > *, .card-image {
			opacity: 0;
			transform: translateX(1.5rem);
			transition-delay: 0s;
			transition-timing-function: cubic-bezier(.5, 0, .75, 0);
		}

		article.card-content > :nth-child(1) {
			transition-delay: .05s;
		}

		article.card-content > :nth-child(2) {
			transition-delay: .1s;
		}

		article.card-content > :nth-child(3) {
			transition-delay: .15s;
		}

		aside.card-content {
			.button, figcaption, blockquote {
				opacity: 1;
				transform: translateX(0);
				transition-timing-function: cubic-bezier(.25, 1, .5, 1);
			}

			blockquote {
				transition-delay: .3s;
			}

			figcaption {
				transition-delay: .35s;
			}

			.button {
				transition-delay: .4s;
			}
		}
	}

	@include desktop {
		&.is-full-width {
			.card-image {
				opacity: 1 !important;
				transform: none !important;
			}

			aside.card-content {
				width: 50%;
				right: 0;
				padding-top: 2rem;
				padding-bottom: 2rem;

				.title {
					font-size: $size-6 !important;
				}

				& > figure.quote > blockquote {
					:deep(p) {
						font-size: $size-5 !important;
					}
				}
			}
		}
	}
}

</style>
